import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Brand } from './Brand';
import { useAuth } from '../lib/auth';
import { SECTIONS, navigate, type Section } from '../lib/router';
import { ROLE_LABELS, PLANS } from '@inbrief/shared';

const NAV: { group: string; items: { section: Section; label: string }[] }[] = [
  {
    group: 'Platform',
    items: [
      { section: 'dashboard', label: 'Dashboard' },
      { section: 'source', label: 'Documents' },
      { section: 'breakdown', label: 'Contract Breakdown' },
      { section: 'ask', label: 'Ask InBrief' },
      { section: 'explain', label: 'Patient Communications' },
      { section: 'translate', label: 'Translation' },
    ],
  },
  {
    group: 'Organisation',
    items: [
      { section: 'admin', label: 'Team & Practices' },
      { section: 'plan', label: 'Plan & Access' },
      { section: 'services', label: 'InBrief Services' },
    ],
  },
];

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');

export function Shell({
  section, children, allowance,
}: {
  section: Section;
  children: ReactNode;
  allowance?: { used: number; limit: number } | null;
}) {
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Close on navigation, and on Escape. The demonstration's menu needed three
  // attempts before it behaved; the working shape is kept exactly — a real
  // backdrop element rather than a pseudo-element (JS cannot bind a click
  // handler to ::after), and both nav groups inside one positioned panel so
  // they cannot overlap each other.
  useEffect(() => { setMenuOpen(false); }, [section]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const pct = allowance && allowance.limit > 0
    ? Math.min(100, Math.round((allowance.used / allowance.limit) * 100))
    : 0;

  return (
    <div className="app-wrapper">
      <aside className={`sidebar${menuOpen ? ' mobile-open' : ''}`} id="sidebar">
        <div className="sidebar-logo">
          <Brand />
          <button
            ref={toggleRef}
            className="mobile-menu-toggle"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            aria-controls="sidebarNav"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        <div className="sidebar-nav" id="sidebarNav">
          {NAV.map((group) => (
            <nav className="nav-section" key={group.group}>
              <div className="nav-section-title">{group.group}</div>
              {group.items.map((item) => (
                <a
                  key={item.section}
                  className={`nav-item${section === item.section ? ' active' : ''}`}
                  href={`#${item.section}`}
                  aria-current={section === item.section ? 'page' : undefined}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          ))}

          {allowance ? (
            <div className="plan-box">
              <div className="plan-name">
                InBrief {PLANS[user?.planId === 'complete' ? 'complete' : 'core'].name}
              </div>
              <div className="plan-meta">
                Translation allowance: {allowance.used.toLocaleString('en-GB')} of{' '}
                {allowance.limit.toLocaleString('en-GB')} used this year
              </div>
              <div className="meter"><span style={{ width: `${pct}%` }} /></div>
            </div>
          ) : null}
        </div>
      </aside>

      <div
        className={`menu-backdrop${menuOpen ? ' active' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <div className="main-content">
        <header className="top-bar">
          <div className="search-bar">
            <label className="visually-hidden" htmlFor="globalSearch">Search</label>
            <input
              id="globalSearch"
              type="search"
              placeholder="Search documents, questions, outputs..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  navigate('ask', { q: (e.target as HTMLInputElement).value });
                }
              }}
            />
          </div>
          <div className="top-bar-actions">
            {user ? (
              <>
                <span className="field-hint" style={{ marginRight: 4 }}>
                  {user.name} — {ROLE_LABELS[user.role]}
                </span>
                <button className="btn btn-ghost btn-small" onClick={signOut}>Sign out</button>
                <div className="user-profile" title={`${user.name} (${user.email})`}>
                  {initials(user.name)}
                </div>
              </>
            ) : null}
          </div>
        </header>

        <main className="content">{children}</main>
      </div>
    </div>
  );
}
